/**
 * Product Detail Modal
 *
 * Opens product detail in a modal window with History API support.
 * Similar functionality to rohlik.cz product modals.
 */

import { updateLazy } from "Functions/lazy";
import { disableScroll, enableScroll } from "Functions/scroll-lock";
import { initTippy } from "Functions/tippy";
import { initializeCartAddSelect } from "Functions/cart-add";
import { fetchUserPrices } from "Functions/fetch-user-prices";

const $ = window.jQuery;

class ProductDetailModal {
    constructor() {
        this.modal = null;
        this.$modal = null;
        this.isOpen = false;
        this.originalUrl = null;
        this.originalTitle = null;
        this.isNavigatingBack = false;
        this.tabsPanelObserver = null;
        this.prefetchEnabled = true; // set false to disable hover prefetch
        this.prefetchCache = new Map();

        this.init();
    }

    init() {
        this.createModalContainer();
        this.attachClickHandlers();
        this.attachPopStateHandler();
        this.attachStackedModalSupport();
        this.loadProductStyles();
    }

    loadProductStyles() {
        // Check if product styles are already loaded
        if (document.querySelector('link[href*="styles-product.css"]')) {
            return;
        }

        // Find version timestamp from existing CSS files
        const existingCss = document.querySelector('link[href*="styles-"]');
        const timestamp = existingCss ? existingCss.href.match(/\?(\d+)/)?.[1] || "" : "";

        // Load product styles CSS
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = `/css/styles-product.css${timestamp ? "?" + timestamp : ""}`;
        document.head.appendChild(link);
    }

    createModalContainer() {
        if (document.getElementById("product-detail-modal")) {
            this.modal = document.getElementById("product-detail-modal");
            this.$modal = $(this.modal);
            this.attachModalEvents();
            return;
        }

        const modalHtml = `
            <div class="modal c-modal c-modal--product-detail fade" id="product-detail-modal" tabindex="-1" role="dialog" aria-hidden="true">
                <div class="modal-dialog modal-dialog--large" role="document">
                    <div class="modal-content">
                        <div class="c-product-detail-modal__loader">
                            <div class="c-loader"></div>
                        </div>
                    </div>
                </div>
            </div>
        `;

        document.body.insertAdjacentHTML("beforeend", modalHtml);
        this.modal = document.getElementById("product-detail-modal");
        this.$modal = $(this.modal);

        this.attachModalEvents();
    }

    attachModalEvents() {
        this.$modal.on("show.bs.modal", () => this.onModalShow());
        this.$modal.on("shown.bs.modal", () => this.onModalShown());
        this.$modal.on("hide.bs.modal", () => this.onModalHide());
        this.$modal.on("hidden.bs.modal", () => this.onModalHidden());
    }

    attachClickHandlers() {
        // Event delegation for product links with modal trigger class
        $(document).on("click", ".js-product-modal-trigger", (e) => {
            // Gift remove/return buttons live inside the modal trigger link
            if ($(e.target).closest("button, .js-remove-gift, .js-return-gift").length) {
                return;
            }

            // Allow ctrl+click or cmd+click to open in new tab
            if (e.ctrlKey || e.metaKey) {
                return;
            }

            e.preventDefault();
            e.stopPropagation();

            const $link = $(e.currentTarget);
            const productUrl = $link.attr("href");

            if (this.isOpen) {
                window.open(productUrl, "_blank", "noopener");
                return;
            }

            this.openModal(productUrl);
        });

        let hoverTimer;
        $(document)
            .on("mouseenter", ".js-product-modal-trigger", (e) => {
                const href = $(e.currentTarget).attr("href");
                hoverTimer = setTimeout(() => this.prefetchProduct(href), 100);
            })
            .on("mouseleave", ".js-product-modal-trigger", () => {
                clearTimeout(hoverTimer);
            });
    }

    prefetchProduct(productUrl) {
        if (!this.prefetchEnabled) return;

        const productId = this.extractProductId(productUrl);
        if (!productId || this.prefetchCache.has(productId)) return;

        const promise = fetch(`/produkt-modal/${productId}`, {
            headers: { "X-Requested-With": "XMLHttpRequest" },
        }).then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.text();
        });

        // Prevent unhandled rejection when user never clicks (prefetch-only case).
        promise.catch(() => {});

        this.prefetchCache.set(productId, promise);
    }

    attachPopStateHandler() {
        window.addEventListener("popstate", (event) => {
            if (!this.isOpen) {
                return;
            }

            // hash navigation to a page-level modal (modal-back.js sets location.hash
            // on every .c-modal show) also fires popstate — opening e.g.
            // #competition-modal from inside the product modal must not close it
            const hashTarget = window.location.hash
                ? document.getElementById(window.location.hash.slice(1))
                : null;

            if (hashTarget && hashTarget.classList.contains("c-modal")) {
                return;
            }

            // back navigation landing on the product modal's own history entry
            // (e.g. closing a stacked modal via the back button)
            if (event.state && event.state.modal) {
                return;
            }

            // Close modal without updating history (we're already going back)
            this.isNavigatingBack = true;
            this.closeModal(false);
        });
    }

    attachStackedModalSupport() {
        // Page-level modals (e.g. competition) opened from inside the product modal
        // stack above it: both share Bootstrap's default z-index (1050) and the product
        // modal container sits later in the DOM, so without a bump the second modal
        // would open invisibly behind the product modal.
        $(document).on("show.bs.modal", ".c-modal", (e) => {
            if (!this.isOpen || e.target === this.modal) {
                return;
            }

            $(e.target).css("z-index", 1060);
        });

        $(document).on("shown.bs.modal", ".c-modal", (e) => {
            if (!this.isOpen || e.target === this.modal) {
                return;
            }

            $(".modal-backdrop").last().css("z-index", 1055);
        });

        $(document).on("hidden.bs.modal", ".c-modal", (e) => {
            if (!this.isOpen || e.target === this.modal) {
                return;
            }

            $(e.target).css("z-index", "");

            // Bootstrap and modal-jump released the page scroll lock when the stacked
            // modal closed — restore it while the product modal stays open
            document.body.classList.add("modal-open");
            disableScroll("product-detail-modal");
        });
    }

    openModal(productUrl) {
        if (this.isOpen) return;

        // Save original URL before opening
        this.originalUrl = window.location.href;
        this.originalTitle = document.title;

        // Show modal with loader
        this.$modal.find(".modal-content").html(`
            <div class="c-product-detail-modal__loader">
                <div class="c-spinner"></div>
            </div>
        `);

        this.$modal.modal({
            backdrop: true,
            keyboard: true,
        });

        history.pushState({ modal: true, productUrl }, "", window.location.href);

        // Load content
        this.loadContent(productUrl);
    }

    extractProductId(productUrl) {
        // URL format: /product-name-p12345 or /product-name-p12345?params
        const match = productUrl.match(/-p(\d+)(?:\?|$)/);
        return match ? match[1] : null;
    }

    async loadContent(productUrl) {
        const productId = this.extractProductId(productUrl);

        try {
            if (!productId) {
                throw new Error("Invalid product URL - cannot extract product ID");
            }

            const cached = this.prefetchCache.get(productId);
            const htmlPromise =
                cached ??
                fetch(`/produkt-modal/${productId}`, {
                    headers: { "X-Requested-With": "XMLHttpRequest" },
                }).then((res) => {
                    if (!res.ok) throw new Error(`Failed to load product: ${res.status}`);
                    return res.text();
                });

            const html = await htmlPromise;
            this.$modal.find(".modal-content").html(html);

            // Initialize components after content is loaded
            this.initModalComponents();
        } catch (error) {
            if (productId) {
                this.prefetchCache.delete(productId);
            }

            // Stay on the current page — classic detail often 404s too (e.g. gift products)
            this.isNavigatingBack = true;
            this.$modal.modal("hide");

            if (typeof Eshop !== "undefined" && typeof Eshop.alert === "function") {
                Eshop.alert("Detail produktu nie je dostupný.", { type: "error" });
            }
        }
    }

    initModalComponents() {
        const $modalContent = this.$modal.find(".modal-content");

        // Update lazy loading for images
        try {
            if (typeof updateLazy === "function") {
                updateLazy();
            } else if (window.updateLazy) {
                window.updateLazy();
            }
        } catch (e) {
            // Silently ignore lazy loading errors
        }

        // Initialize favorite trigger buttons
        $modalContent.find(".js-favorite-trigger").each(function () {
            try {
                if (window.rFavoriteTrigger) {
                    new window.rFavoriteTrigger(this);
                }
            } catch (e) {
                // Silently ignore favorite trigger initialization errors
            }
        });

        // Initialize tippy tooltips with high z-index (modal has z-index ~1050)
        setTimeout(() => {
            try {
                initTippy(".js-tippy", { zIndex: 10000 });
            } catch (e) {
                // Silently ignore tippy initialization errors
            }
        }, 100);

        // Initialize sticky tabs panel for modal scroll
        this.initTabsPanel($modalContent);

        // Initialize Vue ShippingInfo components
        const $shippingInfoEls = $modalContent.find('[data-has-component="shippingInfo"]');
        if ($shippingInfoEls.length) {
            Promise.all([import("Common/vue"), import("VueComponents/ShippingInfo.vue")])
                .then(([{ Vue }, { default: ShippingInfo }]) => {
                    $shippingInfoEls.each(function () {
                        try {
                            new Vue({
                                el: this,
                                components: { ShippingInfo },
                            });
                            this.removeAttribute("data-has-component");
                        } catch (e) {}
                    });
                })
                .catch(() => {});
        }

        // Initialize quick update spinners for cart quantity
        try {
            if (window.initializeSpinners) {
                window.initializeSpinners();
            }
        } catch (e) {}

        // Initialize cart add select tabs
        try {
            initializeCartAddSelect();
        } catch (e) {}

        // Fetch dynamic user prices
        try {
            fetchUserPrices();
        } catch (e) {}

        this.initLazyTabs($modalContent);

        // GA4 view_item — detail v modáli je AJAX bez page loadu, takže PHP tracking
        // helper view_item neodpáli; pushneme ho z ec-data inputu v modáli (LNS-1706)
        try {
            this.trackViewItem($modalContent);
        } catch (e) {
            // tracking nesmie zhodiť modál
        }

        // Dispatch custom event for additional initializations
        try {
            window.rEvent?.dispatch("ProductModal.loaded", {
                modal: this.$modal,
                content: $modalContent,
            });
        } catch (e) {
            // Silently ignore event dispatch errors
        }
    }

    trackViewItem($modalContent) {
        const $ecInput = $modalContent.find('[name="ec-data"][data-ec-item="Products"]').first();

        if (!window.GA4Tracking || !$ecInput.length || !$ecInput.val()) {
            return;
        }

        const data = window.GA4Tracking.mapData(
            window.GA4Tracking.prepareInputDataValue($ecInput.val())
        );

        window.GA4Tracking.trackEvent("view_item", {
            value: data.price,
            currency: window.Eshop.options.currency.code,
            items: [data],
        });
    }

    initLazyTabs($modalContent) {
        const productId = $modalContent.find(".js-product-detail").data("product-id");
        const rTabsEl = $modalContent.find("r-tabs#product-tabs")[0];

        if (!rTabsEl || !productId) return;

        $modalContent.find("[data-lazy-tab]").each((i, panel) => {
            const tabId = panel.dataset.lazyTab;
            let loaded = false;

            rTabsEl.addEventListener("rTab.after.openTab", async (event) => {
                if (loaded || !event.detail.activeTabs.includes(tabId)) return;
                loaded = true;

                try {
                    const res = await fetch(`/produkt-modal/${productId}/tab/${tabId}`, {
                        headers: { "X-Requested-With": "XMLHttpRequest" },
                    });
                    if (!res.ok) throw new Error();
                    panel.innerHTML = await res.text();
                    updateLazy?.();
                } catch (e) {
                    // leave spinner visible on error
                }
            });
        });
    }

    initTabsPanel($modalContent) {
        const target = $modalContent.find("#product-tabs-nav")[0];
        const panel = $modalContent.find(".js-product-tabs-panel")[0];
        const scrollContainer = this.$modal.find(".modal-content")[0];

        if (!target || !panel || !scrollContainer) return;

        // Disconnect existing observer if any
        if (this.tabsPanelObserver) {
            this.tabsPanelObserver.disconnect();
        }

        // Create observer with modal-content as the root
        this.tabsPanelObserver = new IntersectionObserver(
            ([entry]) => {
                // Check if target is above the root's top edge (scrolled past)
                const rootTop = entry.rootBounds?.top || 0;
                const targetBottom = entry.boundingClientRect.bottom;
                const isScrolledPast = targetBottom < rootTop;

                if (!entry.isIntersecting && isScrolledPast) {
                    panel.classList.add("is-active");
                } else {
                    panel.classList.remove("is-active");
                }
            },
            {
                root: scrollContainer,
                threshold: [0, 0.1, 0.5, 1],
            }
        );

        this.tabsPanelObserver.observe(target);
    }

    closeModal(updateHistory = true) {
        this.$modal.modal("hide");

        if (
            updateHistory &&
            this.originalUrl &&
            !this.isNavigatingBack &&
            window.location.href !== this.originalUrl
        ) {
            history.pushState({}, this.originalTitle, this.originalUrl);
        }
    }

    onModalShow() {
        this.isOpen = true;
        disableScroll("product-detail-modal");
    }

    onModalShown() {
        // Modal is fully visible
        this.$modal.addClass("is-loaded");
    }

    onModalHide() {
        // Before closing
    }

    onModalHidden() {
        this.isOpen = false;
        this.isNavigatingBack = false;
        enableScroll("product-detail-modal");

        // Disconnect tabs panel observer
        if (this.tabsPanelObserver) {
            this.tabsPanelObserver.disconnect();
            this.tabsPanelObserver = null;
        }

        // Clear modal content
        this.$modal.find(".modal-content").html("");
        this.$modal.removeClass("is-loaded");

        // Restore original URL if not already done (e.g., when clicking backdrop)
        if (this.originalUrl && window.location.href !== this.originalUrl) {
            history.pushState({}, this.originalTitle, this.originalUrl);
        }

        this.originalUrl = null;
        this.originalTitle = null;
    }
}

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", () => {
    window.productDetailModal = new ProductDetailModal();
});

export default ProductDetailModal;
